import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Users, Clock3, Timer, Wallet, TrendingUp, CheckCircle2, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const PERIODS = ["today", "week", "month", "year", "custom"];

const formatNumber = (value, digits = 1) => Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
});

function MetricCard({ icon: Icon, label, value, note, testid }) {
    return (
        <Card className="p-4 sm:p-5" data-testid={testid}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="mt-2 text-2xl font-bold break-words">{value}</div>
                    {note ? <div className="mt-1 text-xs text-muted-foreground">{note}</div> : null}
                </div>
                <Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            </div>
        </Card>
    );
}

function Section({ title, children }) {
    return <section className="space-y-3"><h2 className="text-lg font-bold">{title}</h2>{children}</section>;
}

export default function WorkforceAnalyticsPage() {
    const { t } = useTranslation();
    const [period, setPeriod] = useState("month");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        if (period === "custom" && (!fromDate || !toDate)) return;
        setLoading(true);
        setError("");
        try {
            const params = { period };
            if (period === "custom") Object.assign(params, { from: fromDate, to: toDate });
            const response = await api.get("/analytics/workforce-earnings", { params });
            setData(response.data);
        } catch (requestError) {
            setError(requestError.response?.status === 403
                ? t("workforce_analytics.errors.owner_only")
                : t("workforce_analytics.errors.load"));
        } finally {
            setLoading(false);
        }
    }, [period, fromDate, toDate, t]);

    useEffect(() => { load(); }, [load]);

    const currency = data?.currency || "PHP";
    const money = useCallback((value) => new Intl.NumberFormat("en-PH", {
        style: "currency", currency, maximumFractionDigits: 2,
    }).format(Number(value || 0)), [currency]);
    const pct = (value) => value === null || value === undefined ? t("workforce_analytics.not_available") : `${formatNumber(value)}%`;
    const perUnit = (value) => value === null || value === undefined ? t("workforce_analytics.not_available") : money(value);
    const workforce = data?.workforce || {};
    const financial = data?.financial_contribution || {};
    const productivity = data?.productivity || {};
    const contributionTrend = useMemo(() => (data?.trends || []).map((row) => ({
        ...row, label: row.period, net_contribution: row.net_earnings,
    })), [data]);
    const labourTrend = useMemo(() => (data?.trends || []).map((row) => ({
        ...row, label: row.period, labour_hours: row.paid_labour_hours,
    })), [data]);
    const departments = useMemo(() => data?.departments || [], [data]);

    return (
        <div className="space-y-6 pb-8" data-testid="workforce-analytics-page">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <p className="tiny-label">{t("workforce_analytics.eyebrow")}</p>
                    <h1 className="text-3xl sm:text-4xl font-bold">{t("workforce_analytics.title")}</h1>
                    <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{t("workforce_analytics.subtitle")}</p>
                </div>
                <div className="flex flex-wrap gap-2" aria-label={t("workforce_analytics.period.label")}>
                    {PERIODS.map((value) => (
                        <Button key={value} size="sm" variant={period === value ? "default" : "outline"}
                            onClick={() => setPeriod(value)} data-testid={`workforce-period-${value}`}>
                            {t(`workforce_analytics.period.${value}`)}
                        </Button>
                    ))}
                </div>
            </div>

            {period === "custom" ? (
                <Card className="p-4 grid gap-3 sm:grid-cols-2" data-testid="workforce-custom-period">
                    <label className="text-sm font-medium">{t("workforce_analytics.period.from")}<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} /></label>
                    <label className="text-sm font-medium">{t("workforce_analytics.period.to")}<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} /></label>
                </Card>
            ) : null}

            <Card className="border-amber-300/60 bg-amber-50/60 p-4 text-sm dark:bg-amber-950/20">
                <div className="flex gap-2"><Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" /><p>{t("workforce_analytics.attribution_notice")}</p></div>
            </Card>

            {loading ? <Card className="p-8 text-center" data-testid="workforce-loading">{t("common.loading")}</Card> : null}
            {error ? <Card className="border-destructive/40 p-4 text-destructive" role="alert">{error}</Card> : null}

            {!loading && !error && data ? <>
                <Section title={t("workforce_analytics.sections.glance")}>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                        <MetricCard icon={Users} label={t("workforce_analytics.metrics.active_workers")} value={workforce.active_workers || 0} testid="workforce-active-workers" />
                        <MetricCard icon={Users} label={t("workforce_analytics.metrics.present_today")} value={workforce.present_workers || 0} testid="workforce-present-today" />
                        <MetricCard icon={Clock3} label={t("workforce_analytics.metrics.labour_hours")} value={formatNumber(workforce.total_labour_hours)} testid="workforce-labour-hours" />
                        <MetricCard icon={Timer} label={t("workforce_analytics.metrics.overtime_hours")} value={formatNumber(workforce.overtime_hours)} testid="workforce-overtime-hours" />
                        <MetricCard icon={Wallet} label={t("workforce_analytics.metrics.labour_cost")} value={money(workforce.labour_cost)} note={t("workforce_analytics.metrics.labour_cost_note")} testid="workforce-labour-cost" />
                    </div>
                </Section>

                <Section title={t("workforce_analytics.sections.contribution")}>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <MetricCard icon={TrendingUp} label={t("workforce_analytics.metrics.revenue")} value={money(financial.revenue)} testid="workforce-revenue" />
                        <MetricCard icon={Wallet} label={t("workforce_analytics.metrics.labour_cost")} value={`−${money(financial.labour_cost)}`} testid="workforce-contribution-labour" />
                        <MetricCard icon={Wallet} label={t("workforce_analytics.metrics.other_costs")} value={`−${money(financial.other_expenses)}`} testid="workforce-other-costs" />
                        <MetricCard icon={TrendingUp} label={t("workforce_analytics.metrics.net_contribution")} value={money(financial.net_earnings)} note={t("workforce_analytics.metrics.farm_wide")} testid="workforce-net-contribution" />
                    </div>
                </Section>

                <Section title={t("workforce_analytics.sections.productivity")}>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <MetricCard icon={CheckCircle2} label={t("workforce_analytics.metrics.tasks_completed")} value={productivity.tasks_completed || 0} />
                        <MetricCard icon={CheckCircle2} label={t("workforce_analytics.metrics.completion_rate")} value={pct(productivity.completion_rate)} />
                        <MetricCard icon={TrendingUp} label={t("workforce_analytics.metrics.revenue_per_hour")} value={perUnit(financial.revenue_per_labour_hour)} note={t("workforce_analytics.metrics.farm_wide")} />
                        <MetricCard icon={Wallet} label={t("workforce_analytics.metrics.labour_cost_per_task")} value={perUnit(productivity.labour_cost_per_completed_task)} />
                    </div>
                </Section>

                <div className="grid gap-4 xl:grid-cols-2">
                    <Card className="p-4 sm:p-5"><h2 className="font-bold">{t("workforce_analytics.charts.contribution")}</h2><div className="mt-4 h-72"><ResponsiveContainer width="100%" height="100%"><LineChart data={contributionTrend}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" /><YAxis /><Tooltip formatter={(v) => money(v)} /><Legend /><Line type="monotone" dataKey="revenue" name={t("workforce_analytics.metrics.revenue")} stroke="#2f6f52" strokeWidth={2} /><Line type="monotone" dataKey="labour_cost" name={t("workforce_analytics.metrics.labour_cost")} stroke="#c28b2c" strokeWidth={2} /><Line type="monotone" dataKey="net_contribution" name={t("workforce_analytics.metrics.net_contribution")} stroke="#2457a7" strokeWidth={2} /></LineChart></ResponsiveContainer></div></Card>
                    <Card className="p-4 sm:p-5"><h2 className="font-bold">{t("workforce_analytics.charts.labour")}</h2><div className="mt-4 h-72"><ResponsiveContainer width="100%" height="100%"><BarChart data={labourTrend}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" /><YAxis /><Tooltip /><Legend /><Bar dataKey="labour_hours" name={t("workforce_analytics.metrics.labour_hours")} fill="#2f6f52" /></BarChart></ResponsiveContainer></div></Card>
                </div>

                <Card className="p-4 sm:p-5"><h2 className="font-bold">{t("workforce_analytics.sections.departments")}</h2><p className="mt-1 text-xs text-muted-foreground">{t("workforce_analytics.department_notice")}</p><div className="mt-4 h-72"><ResponsiveContainer width="100%" height="100%"><BarChart data={departments}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="department" /><YAxis /><Tooltip formatter={(v, name) => name === "labour_cost" ? money(v) : formatNumber(v)} /><Legend /><Bar dataKey="labour_hours" name={t("workforce_analytics.metrics.labour_hours")} fill="#2f6f52" /><Bar dataKey="labour_cost" name={t("workforce_analytics.metrics.labour_cost")} fill="#c28b2c" /></BarChart></ResponsiveContainer></div></Card>
            </> : null}
        </div>
    );
}
